import React, { useEffect, useRef, useState, useCallback } from 'react';

const RealisticBackground = () => {
  const canvasRef = useRef(null);
  const [timeOfDay, setTimeOfDay] = useState('night');
  const [mounted, setMounted] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  
  const getTimeOfDay = useCallback(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 8) return 'dawn';
    if (hour >= 8 && hour < 17) return 'day';
    if (hour >= 17 && hour < 20) return 'dusk';
    return 'night';
  }, []);
  
  useEffect(() => {
    setTimeOfDay(getTimeOfDay());
    setMounted(true);
    const interval = setInterval(() => setTimeOfDay(getTimeOfDay()), 60000);
    
    // Mouse tracking for parallax
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
    };
    
    // Scroll tracking
    const handleScroll = () => setScrollY(window.scrollY);
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [getTimeOfDay]);

  useEffect(() => {
    if (!mounted) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let butterflies = [];
    let raindrops = [];
    let fireflies = [];
    let time = 0;
    let mouse = { x: 0.5, y: 0.5 };
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Track mouse on canvas
    const handleCanvasMouseMove = (e) => {
      mouse.x = e.clientX / canvas.width;
      mouse.y = e.clientY / canvas.height;
    };
    window.addEventListener('mousemove', handleCanvasMouseMove);
    
    // 5 lively butterflies
    const initButterflies = () => {
      butterflies = [];
      const count = window.innerWidth < 768 ? 3 : 5;
      for (let i = 0; i < count; i++) {
        butterflies.push({
          x: 80 + Math.random() * (canvas.width - 160),
          y: 50 + Math.random() * canvas.height * 0.5,
          size: window.innerWidth < 768 ? 4 + Math.random() * 3 : 5 + Math.random() * 4,
          baseSpeedX: (Math.random() - 0.5) * 0.35,
          baseSpeedY: (Math.random() - 0.5) * 0.25,
          speedX: 0, speedY: 0,
          wingPhase: Math.random() * Math.PI * 2,
          wingSpeed: 0.1 + Math.random() * 0.08,
          flickerPhase: Math.random() * Math.PI * 2,
          flickerSpeed: 0.015 + Math.random() * 0.02,
          baseOpacity: 0.6 + Math.random() * 0.35,
          wanderAngle: Math.random() * Math.PI * 2,
          wanderTimer: 0,
          wanderInterval: 800 + Math.random() * 1500,
          burstTimer: 0,
          burstInterval: 3000 + Math.random() * 5000,
          isBursting: false,
          burstSpeedX: 0, burstSpeedY: 0,
          isResting: false, restTimer: 0, restDuration: 0,
          spiralPhase: Math.random() * Math.PI * 2,
          spiralSpeed: 0.008 + Math.random() * 0.015,
          spiralRadius: 15 + Math.random() * 35,
          mouseAttraction: 0.3 + Math.random() * 0.4 // Attracted to mouse
        });
      }
    };
    
    // Fireflies for interactivity
    const initFireflies = () => {
      fireflies = [];
      const count = window.innerWidth < 768 ? 15 : 30;
      for (let i = 0; i < count; i++) {
        fireflies.push({
          x: Math.random() * canvas.width,
          y: canvas.height * 0.4 + Math.random() * canvas.height * 0.5,
          size: 1 + Math.random() * 2,
          speedX: (Math.random() - 0.5) * 0.5,
          speedY: (Math.random() - 0.5) * 0.3,
          phase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.02 + Math.random() * 0.03,
          baseOpacity: 0.3 + Math.random() * 0.5
        });
      }
    };
    
    // Rain
    const initRain = () => {
      raindrops = [];
      const count = window.innerWidth < 768 ? 50 : (timeOfDay === 'night' ? 100 : 60);
      for (let i = 0; i < count; i++) {
        raindrops.push({
          x: Math.random() * canvas.width * 1.3,
          y: Math.random() * canvas.height,
          length: 8 + Math.random() * 12,
          speed: 5 + Math.random() * 4,
          opacity: 0.03 + Math.random() * 0.06,
          thickness: 0.3 + Math.random() * 0.4
        });
      }
    };
    
    initButterflies();
    initFireflies();
    initRain();
    
    // Draw butterfly with mouse interaction
    const drawButterfly = (b, deltaTime) => {
      // Resting
      if (b.isResting) {
        b.restTimer += deltaTime;
        if (b.restTimer > b.restDuration) { b.isResting = false; b.restTimer = 0; }
      } else if (Math.random() < 0.00015) {
        b.isResting = true;
        b.restDuration = 1500 + Math.random() * 3000;
      }
      
      // Burst
      b.burstTimer += deltaTime;
      if (b.burstTimer > b.burstInterval && !b.isResting) {
        b.isBursting = true;
        b.burstSpeedX = (Math.random() - 0.5) * 4;
        b.burstSpeedY = (Math.random() - 0.5) * 3;
        b.burstTimer = 0;
        b.burstInterval = 3000 + Math.random() * 5000;
        setTimeout(() => { b.isBursting = false; }, 250 + Math.random() * 300);
      }
      
      b.flickerPhase += b.flickerSpeed;
      const flicker = 0.45 + Math.sin(b.flickerPhase) * 0.35 + Math.sin(b.flickerPhase * 3.1) * 0.2;
      const currentOpacity = b.baseOpacity * flicker;
      
      const wingMult = b.isResting ? 0.12 : (b.isBursting ? 3 : 1);
      b.wingPhase += b.wingSpeed * wingMult;
      const wingFlap = Math.sin(b.wingPhase);
      const wingScale = b.isResting ? 0.94 : (0.35 + Math.abs(Math.cos(b.wingPhase * 0.5)) * 0.65);
      
      ctx.save();
      ctx.translate(b.x, b.y);
      const moveAngle = Math.atan2(b.speedY, b.speedX) * 0.12;
      ctx.rotate(moveAngle);
      
      // Glow
      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, b.size * 4);
      glow.addColorStop(0, `rgba(235, 245, 255, ${currentOpacity * 0.4})`);
      glow.addColorStop(0.5, `rgba(215, 230, 250, ${currentOpacity * 0.15})`);
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(0, 0, b.size * 4, 0, Math.PI * 2);
      ctx.fill();
      
      const wingAngle = wingFlap * 0.6;
      
      // Wings
      ctx.fillStyle = `rgba(252, 254, 255, ${currentOpacity * 0.95})`;
      ctx.beginPath();
      ctx.ellipse(-b.size * 0.48, -b.size * 0.2, b.size * 0.8 * wingScale, b.size * 0.45, -0.25 + wingAngle * 0.38, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(b.size * 0.48, -b.size * 0.2, b.size * 0.8 * wingScale, b.size * 0.45, 0.25 - wingAngle * 0.38, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = `rgba(240, 248, 255, ${currentOpacity * 0.8})`;
      ctx.beginPath();
      ctx.ellipse(-b.size * 0.3, b.size * 0.14, b.size * 0.45 * wingScale, b.size * 0.3, -0.15 + wingAngle * 0.25, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(b.size * 0.3, b.size * 0.14, b.size * 0.45 * wingScale, b.size * 0.3, 0.15 - wingAngle * 0.25, 0, Math.PI * 2);
      ctx.fill();
      
      // Body
      ctx.fillStyle = `rgba(220, 230, 245, ${currentOpacity * 0.9})`;
      ctx.beginPath();
      ctx.ellipse(0, 0, b.size * 0.06, b.size * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
      
      // Movement with mouse attraction
      if (!b.isResting) {
        b.wanderTimer += deltaTime;
        if (b.wanderTimer > b.wanderInterval) {
          b.wanderAngle += (Math.random() - 0.5) * 2.5;
          b.wanderTimer = 0;
          b.wanderInterval = 800 + Math.random() * 1500;
        }
        
        b.spiralPhase += b.spiralSpeed;
        const spiralX = Math.cos(b.spiralPhase) * b.spiralRadius * 0.012;
        const spiralY = Math.sin(b.spiralPhase) * b.spiralRadius * 0.01;
        
        // Mouse attraction
        const mouseWorldX = mouse.x * canvas.width;
        const mouseWorldY = mouse.y * canvas.height;
        const dx = mouseWorldX - b.x;
        const dy = mouseWorldY - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let attractX = 0, attractY = 0;
        if (dist < 300 && dist > 50) {
          attractX = (dx / dist) * b.mouseAttraction * 0.08;
          attractY = (dy / dist) * b.mouseAttraction * 0.06;
        } else if (dist <= 50) {
          // Flee from mouse when too close
          attractX = -(dx / dist) * 0.5;
          attractY = -(dy / dist) * 0.4;
        }
        
        if (b.isBursting) {
          b.speedX = b.burstSpeedX;
          b.speedY = b.burstSpeedY;
          b.burstSpeedX *= 0.9;
          b.burstSpeedY *= 0.9;
        } else {
          b.speedX = b.baseSpeedX + Math.cos(b.wanderAngle) * 0.15 + spiralX + attractX + Math.sin(time * 0.0004) * 0.05;
          b.speedY = b.baseSpeedY + Math.sin(b.wanderAngle) * 0.1 + spiralY + attractY + Math.cos(time * 0.0003) * 0.04;
        }
        
        b.x += b.speedX;
        b.y += b.speedY;
      }
      
      // Bounds
      const margin = 30;
      if (b.x < margin) { b.x = margin; b.wanderAngle = Math.random() * Math.PI - Math.PI/2; b.baseSpeedX = Math.abs(b.baseSpeedX); }
      if (b.x > canvas.width - margin) { b.x = canvas.width - margin; b.wanderAngle = Math.PI/2 + Math.random() * Math.PI; b.baseSpeedX = -Math.abs(b.baseSpeedX); }
      if (b.y < 40) { b.y = 40; b.wanderAngle = Math.random() * Math.PI; b.baseSpeedY = Math.abs(b.baseSpeedY); }
      if (b.y > canvas.height * 0.55) { b.y = canvas.height * 0.55; b.wanderAngle = -Math.random() * Math.PI; b.baseSpeedY = -Math.abs(b.baseSpeedY); }
    };
    
    // Draw fireflies with mouse interaction
    const drawFirefly = (f) => {
      f.phase += f.pulseSpeed;
      const pulse = 0.3 + Math.sin(f.phase) * 0.7;
      
      // Mouse interaction - fireflies scatter from cursor
      const mouseWorldX = mouse.x * canvas.width;
      const mouseWorldY = mouse.y * canvas.height;
      const dx = f.x - mouseWorldX;
      const dy = f.y - mouseWorldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist < 150) {
        f.speedX += (dx / dist) * 0.15;
        f.speedY += (dy / dist) * 0.1;
      }
      
      // Dampen and move
      f.speedX *= 0.98;
      f.speedY *= 0.98;
      f.speedX += (Math.random() - 0.5) * 0.1;
      f.speedY += (Math.random() - 0.5) * 0.08;
      
      f.x += f.speedX;
      f.y += f.speedY;
      
      // Bounds
      if (f.x < 0) f.x = canvas.width;
      if (f.x > canvas.width) f.x = 0;
      if (f.y < canvas.height * 0.35) f.y = canvas.height * 0.35;
      if (f.y > canvas.height) f.y = canvas.height * 0.35;
      
      // Draw
      const opacity = f.baseOpacity * pulse;
      const gradient = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.size * 5);
      gradient.addColorStop(0, `rgba(255, 250, 200, ${opacity})`);
      gradient.addColorStop(0.3, `rgba(255, 245, 180, ${opacity * 0.5})`);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(f.x, f.y, f.size * 5, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = `rgba(255, 255, 220, ${opacity})`;
      ctx.beginPath();
      ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
      ctx.fill();
    };
    
    let lastTime = performance.now();
    
    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      time = currentTime;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Rain
      raindrops.forEach(drop => {
        const gradient = ctx.createLinearGradient(drop.x, drop.y, drop.x - 0.6, drop.y + drop.length);
        gradient.addColorStop(0, 'rgba(180, 195, 220, 0)');
        gradient.addColorStop(0.5, `rgba(195, 210, 230, ${drop.opacity * 0.5})`);
        gradient.addColorStop(1, `rgba(210, 225, 245, ${drop.opacity})`);
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = drop.thickness;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(drop.x, drop.y);
        ctx.lineTo(drop.x - 0.6, drop.y + drop.length);
        ctx.stroke();
        
        drop.y += drop.speed;
        drop.x -= 0.4;
        
        if (drop.y > canvas.height) {
          drop.y = -drop.length - Math.random() * 40;
          drop.x = Math.random() * canvas.width * 1.3;
        }
      });
      
      // Fireflies
      fireflies.forEach(drawFirefly);
      
      // Butterflies
      butterflies.forEach(b => drawButterfly(b, deltaTime));
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate(performance.now());
    
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleCanvasMouseMove);
    };
  }, [mounted, timeOfDay]);
  
  // Parallax calculations
  const parallaxX = (mousePos.x - 0.5) * 20;
  const parallaxY = (mousePos.y - 0.5) * 10;
  const scrollParallax = scrollY * 0.3;
  
  const getCelestialStyle = () => {
    const base = { position: 'absolute', borderRadius: '50%', transition: 'all 3s ease', pointerEvents: 'none' };
    const moonParallaxX = (mousePos.x - 0.5) * 15;
    const moonParallaxY = (mousePos.y - 0.5) * 8;
    
    switch (timeOfDay) {
      case 'dawn':
        return { ...base, width: 'clamp(60px, 8vw, 90px)', height: 'clamp(60px, 8vw, 90px)', right: '12%', top: `calc(28% - ${scrollParallax * 0.1}px)`,
          transform: `translate(${moonParallaxX}px, ${moonParallaxY}px)`,
          background: 'radial-gradient(circle at 40% 40%, #fff5e6 0%, #ffd699 30%, #ffb347 60%, #ff8c00 100%)',
          boxShadow: '0 0 80px 40px rgba(255,180,100,0.35), 0 0 150px 80px rgba(255,140,50,0.2)' };
      case 'day':
        return { ...base, width: 'clamp(50px, 6vw, 75px)', height: 'clamp(50px, 6vw, 75px)', right: '18%', top: `calc(12% - ${scrollParallax * 0.1}px)`,
          transform: `translate(${moonParallaxX}px, ${moonParallaxY}px)`,
          background: 'radial-gradient(circle at 35% 35%, #ffffff 0%, #fffde8 40%, #fff4b8 70%, #ffe066 100%)',
          boxShadow: '0 0 100px 50px rgba(255,255,200,0.5), 0 0 180px 90px rgba(255,245,150,0.3)' };
      case 'dusk':
        return { ...base, width: 'clamp(55px, 7vw, 85px)', height: 'clamp(55px, 7vw, 85px)', left: '10%', top: `calc(32% - ${scrollParallax * 0.1}px)`,
          transform: `translate(${moonParallaxX}px, ${moonParallaxY}px)`,
          background: 'radial-gradient(circle at 45% 45%, #ffe4c4 0%, #ffb07c 35%, #ff7f50 65%, #dc4030 100%)',
          boxShadow: '0 0 70px 35px rgba(255,140,80,0.4), 0 0 130px 65px rgba(255,100,50,0.25)' };
      default:
        return { ...base, width: 'clamp(65px, 8vw, 95px)', height: 'clamp(65px, 8vw, 95px)', right: '14%', top: `calc(8% - ${scrollParallax * 0.1}px)`,
          transform: `translate(${moonParallaxX}px, ${moonParallaxY}px)`,
          background: `radial-gradient(circle at 32% 32%, #fefefe 0%, #f5f5f8 15%, #e8e8ef 30%, #dddde8 45%, #d0d0dc 60%, #c5c5d0 75%, #babac5 100%)`,
          boxShadow: `0 0 60px 30px rgba(230,235,250,0.2), 0 0 120px 60px rgba(210,220,245,0.12), inset -20px -12px 40px rgba(100,105,125,0.5), inset 8px 5px 25px rgba(255,255,255,0.4)` };
    }
  };

  const getSkyGradient = () => {
    switch (timeOfDay) {
      case 'dawn': return 'linear-gradient(to bottom, #1a1428 0%, #2d2440 15%, #4a3a55 30%, #7a5560 50%, #b87060 70%, #e89850 85%, #ffb040 100%)';
      case 'day': return 'linear-gradient(to bottom, #1a3050 0%, #2a5070 20%, #4080a0 45%, #60a0c0 70%, #80c0d8 100%)';
      case 'dusk': return 'linear-gradient(to bottom, #151525 0%, #252535 15%, #3a3050 30%, #604060 50%, #905050 70%, #c06040 85%, #e08030 100%)';
      default: return 'linear-gradient(to bottom, #050508 0%, #08090f 15%, #0b0d15 30%, #0e1118 50%, #111520 70%, #141822 100%)';
    }
  };

  const stars = React.useMemo(() => {
    const count = timeOfDay === 'night' ? 250 : 80;
    return [...Array(count)].map((_, i) => ({
      id: i, left: Math.random() * 100, top: Math.random() * 55,
      size: Math.random() * 2.5 + 0.5, baseOpacity: Math.random() * 0.6 + 0.4,
      animDuration: 1 + Math.random() * 5, animDelay: Math.random() * 7,
      twinkleType: Math.floor(Math.random() * 4)
    }));
  }, [timeOfDay]);

  return (
    <div className="fixed inset-0" style={{ zIndex: 0 }}>
      {/* Sky with parallax */}
      <div className="absolute inset-0 transition-all duration-[5000ms]" 
        style={{ background: getSkyGradient(), transform: `translate(${parallaxX * 0.1}px, ${parallaxY * 0.1}px)` }} />
      
      {/* Stars */}
      {(timeOfDay === 'night' || timeOfDay === 'dusk' || timeOfDay === 'dawn') && (
        <div className="absolute inset-0 overflow-hidden" style={{ transform: `translate(${parallaxX * 0.15}px, ${parallaxY * 0.1}px)` }}>
          {stars.map((star) => (
            <div key={star.id} className="absolute rounded-full" style={{
              width: `${star.size}px`, height: `${star.size}px`,
              left: `${star.left}%`, top: `${star.top}%`,
              background: `rgba(255, 255, 255, ${star.baseOpacity})`,
              animation: `twinkle${star.twinkleType} ${star.animDuration}s ease-in-out infinite`,
              animationDelay: `${star.animDelay}s`,
              boxShadow: star.size > 2 ? `0 0 ${star.size * 2}px rgba(255,255,255,0.3)` : 'none'
            }} />
          ))}
        </div>
      )}
      
      {/* Moon/Sun with parallax */}
      <div style={getCelestialStyle()}>
        {timeOfDay === 'night' && (<>
          <div style={{ position: 'absolute', width: '18%', height: '18%', borderRadius: '50%', background: 'radial-gradient(circle at 40% 40%, rgba(130,135,155,0.35), rgba(110,115,135,0.2))', top: '18%', left: '15%' }} />
          <div style={{ position: 'absolute', width: '12%', height: '12%', borderRadius: '50%', background: 'radial-gradient(circle at 40% 40%, rgba(125,130,150,0.3), rgba(105,110,130,0.15))', top: '45%', left: '28%' }} />
          <div style={{ position: 'absolute', width: '22%', height: '22%', borderRadius: '50%', background: 'radial-gradient(circle at 35% 35%, rgba(135,140,160,0.25), rgba(115,120,140,0.12))', top: '55%', left: '48%' }} />
          <div style={{ position: 'absolute', width: '9%', height: '9%', borderRadius: '50%', background: 'rgba(120,125,145,0.28)', top: '30%', left: '55%' }} />
        </>)}
      </div>
      
      {/* Mountain with parallax */}
      <div className="absolute inset-0 transition-all duration-300" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1920&q=90)',
        backgroundSize: 'cover', backgroundPosition: 'center 20%',
        opacity: timeOfDay === 'night' ? 0.25 : 0.45,
        filter: timeOfDay === 'night' ? 'brightness(0.4) saturate(0.4)' : 'brightness(0.6) saturate(0.7)',
        transform: `translate(${parallaxX * 0.2}px, ${parallaxY * 0.15 - scrollParallax * 0.15}px)`
      }} />
      
      {/* Forest Layers with parallax - 6 layers for dense effect */}
      {[
        { img: 'photo-1476231682828-37e571bc172f', pos: '60%', op: [0.28, 0.38], mask: '60%', speed: 14, px: 0.25, scroll: 0.2 },
        { img: 'photo-1542273917363-3b1817f69a2d', pos: '65%', op: [0.35, 0.45], mask: '55%', speed: 12, px: 0.3, scroll: 0.25 },
        { img: 'photo-1448375240586-882707db888b', pos: 'bottom', op: [0.42, 0.52], mask: '48%', speed: 10, px: 0.35, scroll: 0.3 },
        { img: 'photo-1503435824048-a799a3a84bf7', pos: 'bottom', op: [0.5, 0.6], mask: '42%', speed: 8, px: 0.4, scroll: 0.35 },
        { img: 'photo-1425913397330-cf8af2ff40a1', pos: 'bottom', op: [0.55, 0.65], mask: '35%', speed: 6, px: 0.45, scroll: 0.4 },
        { img: 'photo-1440342359743-84fcb8c21f21', pos: 'bottom', op: [0.6, 0.7], mask: '28%', speed: 4, px: 0.5, scroll: 0.45 }
      ].map((layer, i) => (
        <div key={i} className="absolute inset-0" style={{
          backgroundImage: `url(https://images.unsplash.com/${layer.img}?w=1920&q=90)`,
          backgroundSize: 'cover', backgroundPosition: `center ${layer.pos}`,
          opacity: timeOfDay === 'night' ? layer.op[0] : layer.op[1],
          filter: timeOfDay === 'night' ? `brightness(${0.25 + i * 0.02}) saturate(0.35)` : `brightness(${0.4 + i * 0.03}) saturate(0.55)`,
          maskImage: `linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) ${15 - i * 2}%, rgba(0,0,0,0.4) ${30 - i * 3}%, rgba(0,0,0,0) ${layer.mask})`,
          WebkitMaskImage: `linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) ${15 - i * 2}%, rgba(0,0,0,0.4) ${30 - i * 3}%, rgba(0,0,0,0) ${layer.mask})`,
          transform: `translate(${parallaxX * layer.px}px, ${parallaxY * layer.px * 0.5 - scrollParallax * layer.scroll}px)`,
          animation: `treeSway${i + 1} ${layer.speed}s ease-in-out infinite`
        }} />
      ))}
      
      {/* Fog with parallax */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: timeOfDay === 'night' 
          ? 'linear-gradient(to top, rgba(8,12,18,0.8) 0%, rgba(6,10,15,0.5) 15%, transparent 40%)'
          : 'linear-gradient(to top, rgba(20,30,40,0.6) 0%, rgba(30,40,50,0.3) 15%, transparent 40%)',
        transform: `translate(${parallaxX * 0.3}px, ${-scrollParallax * 0.2}px)`,
        animation: 'fogDrift 50s ease-in-out infinite'
      }} />
      
      {/* Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }} />
      
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.6) 100%)' }} />
      
      <style>{`
        @keyframes twinkle0 { 0%, 100% { opacity: 0.15; transform: scale(0.7); } 50% { opacity: 1; transform: scale(1.4); } }
        @keyframes twinkle1 { 0%, 100% { opacity: 0.25; transform: scale(1); } 30% { opacity: 1; transform: scale(1.5); } 70% { opacity: 0.4; transform: scale(0.85); } }
        @keyframes twinkle2 { 0%, 100% { opacity: 0.35; } 25% { opacity: 0.9; } 50% { opacity: 0.15; } 75% { opacity: 1; } }
        @keyframes twinkle3 { 0%, 100% { opacity: 0.5; transform: scale(1.1); } 40% { opacity: 0.2; transform: scale(0.8); } 80% { opacity: 1; transform: scale(1.3); } }
        @keyframes fogDrift { 0%, 100% { transform: translateX(-3%); opacity: 0.85; } 50% { transform: translateX(3%); opacity: 1; } }
        @keyframes treeSway1 { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(0.1%); } }
        @keyframes treeSway2 { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-0.12%); } }
        @keyframes treeSway3 { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(0.15%); } }
        @keyframes treeSway4 { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-0.18%); } }
        @keyframes treeSway5 { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(0.22%); } }
        @keyframes treeSway6 { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-0.26%); } }
      `}</style>
    </div>
  );
};

export default RealisticBackground;
